import { Label } from "@radix-ui/react-label";
import { useId, useMemo, type ComponentProps } from "react";
import {
    get,
    useFormContext,
    type FieldValues,
    type Path,
} from "react-hook-form";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { useSchema } from "@/v2/hooks/SchemaContext";
import RequiredMark from "./RequiredMark";
import { ZodArray } from "zod";

type FormFieldBaseProps = ComponentProps<typeof Input>;
interface ArFormFieldProps<T extends FieldValues> extends FormFieldBaseProps {
    name: Path<T>;
    label: string;
    labelAlign?: "items-end" | "items-start" | "items-center";
}

export function ArFormField<T extends FieldValues>({
    label,
    name,
    placeholder,
    labelAlign,
    ...rest
}: ArFormFieldProps<T>) {
    const {
        register,
        formState: { errors },
    } = useFormContext<T>();

    const error = get(errors, name);
    const id = useId();
    const schema = useSchema();
    const isRequired = useMemo(() => {
        const parts = name.split(".");
        let currentSchema: any = schema;

        for (const part of parts) {
            if (!currentSchema?.shape) return false;

            // Handle array index (numeric parts)
            if (/^\d+$/.test(part)) {
                if (currentSchema instanceof ZodArray) {
                    currentSchema = currentSchema.element;
                }
                continue;
            }

            currentSchema = currentSchema.shape[part];

            if (currentSchema instanceof ZodArray) {
                currentSchema = currentSchema.element;
            }
        }

        const testResult = currentSchema?.safeParse("");
        return !testResult?.success;
    }, [schema, name]);
    return (
        <div className={cn("flex flex-col gap-1", labelAlign && labelAlign)}>
            <Label
                htmlFor={id}
                className={cn(
                    "text-sm font-medium",
                    error && "text-destructive",
                )}
            >
                {label}
                {isRequired && <RequiredMark />}
            </Label>
            <Input
                placeholder={placeholder}
                className={cn(
                    error &&
                        "border-destructive focus-visible:ring-destructive",
                )}
                {...register(name)}
                {...rest}
            />
            {error && (
                <p id={`${id}-error`} className="text-xs text-destructive">
                    {error.message as string}
                </p>
            )}
        </div>
    );
}
